import React, { useContext, useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { AuthContext } from '../context/authContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Helmet from '../components/Helmet/Helmet';
import { FaMagic, FaDownload, FaShare, FaEye, FaImage, FaPalette, FaGem, FaRobot, FaCrown, FaLightbulb } from 'react-icons/fa';
import { getApiUrl, getMediaUrl } from '../utils/site';
import '../styles/luxury-pages.css';
import '../styles/world-class-luxury.css';

export default function GenerateImage() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [image, setImage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedImages, setGeneratedImages] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Fetch existing generated images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await axios.get(getApiUrl('/api/gallery'));
        setGeneratedImages(res.data || []);
      } catch (err) {
        console.error('Failed to fetch images:', err);
      }
    };
    fetchImages();
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a description for your jewelry');
      return;
    }
    
    setIsGenerating(true);
    setError('');
    setImage('');
    
    try {
      const res = await axios.post(getApiUrl('/api/generate'), { prompt });
      setImage(getMediaUrl(res.data.image_url));
      // Refresh the gallery
      const galleryRes = await axios.get(getApiUrl('/api/gallery'));
      setGeneratedImages(galleryRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate image');
    }
    setIsGenerating(false);
  };

  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'roha-jewelry-design.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareImage = async (imageUrl) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Roha Jewellery AI Generated Design',
          text: 'Check out this beautiful jewelry design generated by AI!',
          url: imageUrl
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(imageUrl);
      alert('Image URL copied to clipboard!');
    }
  };

  if (loading) return (
    <div className="text-center my-5">
      <div className="spinner-border text-gold" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 text-secondary">Loading AI Generator...</p>
    </div>
  );
  
  if (!user || user.role !== 'admin') return null;

  return (
    <>
      <Helmet>
        <title>AI Jewelry Image Generator | Roha Jewellery Admin Dashboard</title>
        <meta name="description" content="Generate beautiful jewelry designs using AI technology. Create custom Ethiopian gemstone jewelry concepts with our advanced AI image generator." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <main className="luxury-page">
        {/* Hero Section */}
        <section className="luxury-page__hero">
          <div className="luxury-page__hero-bg">
            <img src="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="AI Jewelry Design Background" />
          </div>
          <div className="luxury-page__hero-overlay"></div>
          <div className="luxury-page__hero-gradient"></div>
          
          <Container>
            <div className="luxury-page__hero-content">
              <div className="luxury-page__hero-eyebrow">
                <FaRobot /> AI Design Studio
              </div>
              
              <h1 className="luxury-page__hero-title">
                Create <span className="luxury-page__hero-title-gradient">Luxury Jewelry</span> with AI
              </h1>
              
              <p className="luxury-page__hero-subtitle">
                Transform your vision into exquisite Ethiopian gemstone jewelry designs using our advanced AI technology. 
                Generate custom concepts for rings, necklaces, earrings, and more with the precision of artificial intelligence.
              </p>
              
              <div className="luxury-page__hero-actions">
                <button 
                  className="wc-button wc-button-primary wc-button-lg"
                  onClick={() => document.getElementById('generator-section').scrollIntoView({ behavior: 'smooth' })}
                >
                  <FaMagic /> Start Creating
                </button>
                <button className="wc-button wc-button-secondary">
                  <FaCrown /> View Gallery
                </button>
              </div>
              
              <div className="luxury-page__hero-tags">
                <span className="luxury-page__hero-tag">Ethiopian Opal</span>
                <span className="luxury-page__hero-tag">Gold & Silver</span>
                <span className="luxury-page__hero-tag">Gemstone Designs</span>
                <span className="luxury-page__hero-tag">Custom Concepts</span>
              </div>
            </div>
          </Container>
        </section>

        {/* Generator Section */}
        <section className="luxury-section luxury-section--soft" id="generator-section">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaPalette /> AI Generator
              </div>
              <h2 className="luxury-section__title">Create Your Custom Design</h2>
              <p className="luxury-section__subtitle">
                Describe your dream jewelry piece and watch AI bring it to life. Be specific about materials, gemstones, style, and details for the best results.
              </p>
            </div>
            
            <div className="luxury-ai-generator">
              <div className="luxury-ai-generator__header">
                <div className="luxury-ai-generator__icon">
                  <FaMagic />
                </div>
                <div>
                  <h3 className="luxury-ai-generator__title">AI Design Generator</h3>
                  <p className="luxury-ai-generator__subtitle">Powered by advanced machine learning</p>
                </div>
              </div>
              
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              
              <div className="luxury-ai-prompt">
                <label className="luxury-ai-prompt__label">Describe your jewelry design:</label>
                <textarea
                  className="luxury-ai-prompt__textarea"
                  rows="4"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Elegant gold ring with Ethiopian opal gemstone, vintage style, intricate filigree work, luxury jewelry design with diamond accents..."
                />
                <span className="luxury-ai-prompt__hint">
                  💡 Tip: Include materials, gemstones, style, era, and specific details for better results
                </span>
              </div>
              
              <div className="luxury-ai-actions">
                <button
                  className="luxury-ai-button luxury-ai-button--primary"
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim()}
                >
                  {isGenerating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <FaMagic className="me-2" />
                      Generate Design
                    </>
                  )}
                </button>
                
                <button
                  className="luxury-ai-button luxury-ai-button--secondary"
                  onClick={() => setPrompt('')}
                  disabled={isGenerating}
                >
                  Clear Prompt
                </button>
              </div>
              
              {/* Generated Image Display */}
              {image && (
                <div className="luxury-ai-result">
                  <div className="luxury-ai-result__badge">
                    <FaGem /> AI Generated Design
                  </div>
                  
                  <img
                    src={image}
                    alt="Generated jewelry design"
                    className="luxury-ai-result__image"
                  />
                  
                  <div className="luxury-ai-result__actions">
                    <button
                      className="luxury-ai-button luxury-ai-button--primary"
                      onClick={() => downloadImage(image, `roha-jewelry-${Date.now()}.png`)}
                    >
                      <FaDownload className="me-2" />
                      Download
                    </button>
                    <button
                      className="luxury-ai-button luxury-ai-button--secondary"
                      onClick={() => shareImage(image)}
                    >
                      <FaShare className="me-2" />
                      Share
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Container>
        </section>

        {/* Gallery Section */}
        <section className="luxury-section">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaEye /> Design Gallery
              </div>
              <h2 className="luxury-section__title">Recent AI Creations</h2>
              <p className="luxury-section__subtitle">
                Explore designs generated by our AI system. Click any image to view it in detail.
              </p>
            </div>
            
            {generatedImages.length > 0 ? (
              <div className="luxury-ai-gallery">
                {generatedImages.slice(0, 12).map((img, index) => (
                  <div 
                    key={index} 
                    className="luxury-ai-gallery__item"
                    onClick={() => setImage(getMediaUrl(img.image_url))}
                  >
                    <img
                      src={getMediaUrl(img.image_url)}
                      alt={`Generated design ${index + 1}`}
                      className="luxury-ai-gallery__image"
                    />
                    <div className="luxury-ai-gallery__overlay">
                      <span className="luxury-ai-gallery__view">View Design</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <FaImage size={64} className="text-muted mb-4" />
                <h4 className="text-secondary mb-3">No designs generated yet</h4>
                <p className="text-muted">Start creating beautiful jewelry designs above!</p>
              </div>
            )}
          </Container>
        </section>

        {/* Quick Prompts Section */}
        <section className="luxury-section luxury-section--soft">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaLightbulb /> Inspiration
              </div>
              <h2 className="luxury-section__title">Quick Design Prompts</h2>
              <p className="luxury-section__subtitle">
                Need inspiration? Try these pre-written prompts to get started.
              </p>
            </div>
            
            <div className="luxury-grid luxury-grid--2">
              {[
                "Ethiopian opal engagement ring, 18k gold setting, vintage filigree work, luxury bridal jewelry",
                "Emerald necklace, art deco style, platinum chain, diamond accents, evening wear luxury",
                "Ruby earrings, modern minimalist style, geometric shapes, 14k gold, statement pieces",
                "Sapphire bracelet, Edwardian inspired, intricate metalwork, mixed gemstone accents",
                "Diamond solitaire ring, classic design, platinum band, brilliant cut center stone",
                "Pearl necklace, traditional Ethiopian style, gold clasps, cultural heritage design",
                "Amethyst pendant, contemporary design, silver chain, minimalist luxury",
                "Mixed gemstone cocktail ring, bold statement, gold setting, colorful arrangement"
              ].map((quickPrompt, index) => (
                <div key={index} className="luxury-card">
                  <div className="luxury-card__body">
                    <p className="luxury-card__description">{quickPrompt}</p>
                    <button
                      className="luxury-card__link"
                      onClick={() => setPrompt(quickPrompt)}
                    >
                      Use This Prompt <span>→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>
      </main>
    </>
  );
}
